// Account management.
//
//   admin      — every account; role + brand edited together (the DB enforces the pair):
//                  agent              — a brand is REQUIRED
//                  qms / supervisor   — OPTIONAL: empty = cross-brand (the default since
//                                       migration 013, owner 2026-09-21), set = locked to it
//                  admin              — never carries a brand
//   supervisor — ONLY agent/supervisor accounts (owner 2026-09): the role picker drops
//                qms/admin. A brand-LOCKED supervisor also has the brand fixed to their own,
//                so the form can only express what the server (canManageTarget) will accept;
//                the account list is already scoped server-side the same way.
//
// Edit (Task Board #294 Vegas / #295 Prime, asya 2026-10-29 "i can't edit users name/brand"): the
// pencil turns a row into a form for name, role and brand — the PATCH route always took them, the
// screen did not. Same option rules as the create form; the server fence (canManageTarget, both the
// current and the proposed role/brand) is what actually decides. Your own role is not editable here
// (a role change ends that account's sessions — it would log you out mid-edit).
// Agent names are stored in Turkish UPPER CASE (server: displayNameFor; the form shows it as you type).

import { useState } from 'react';
import * as api from '../api';
import type { ManagedUser, Meta, PanelUser, Role } from '../types';
import { useAsync } from '../hooks';
import { Avatar, Callout, Empty, Icon, Loading, Note, PageHead, Panel, Pill } from '../ui';
import { ROLE_TONE } from '../format';

type BrandMode = 'required' | 'optional' | 'none';

const ROLE_OPTIONS: { value: Role; label: string; brand: BrandMode }[] = [
  { value: 'agent', label: 'Agent — sadece kendi skorlarını görür', brand: 'required' },
  { value: 'qms', label: 'QMS Uzmanı — yalnız değerlendirir; marka seçilirse o markaya kilitlenir', brand: 'optional' },
  { value: 'supervisor', label: 'Süpervizör — değerlendirir, itiraz yanıtlar, kullanıcı yönetir; marka seçilirse o markaya kilitlenir', brand: 'optional' },
  { value: 'admin', label: 'Yönetici — her şey', brand: 'none' },
];

/** The server's agent-name rule (users.js displayNameFor), mirrored so the field shows what will be saved. */
const nameAs = (role: Role, name: string) => (role === 'agent' ? name.toLocaleUpperCase('tr-TR') : name);

interface EditDraft { id: number; displayName: string; role: Role; brandKey: string }

/** Short role names for the row editor's picker (the server's ROLE_LABELS). */
const ROLE_NAME: Record<Role, string> = { agent: 'Agent', qms: 'QMS Uzmanı', supervisor: 'Süpervizör', admin: 'Yönetici' };

export default function Users({ user, meta }: { user: PanelUser; meta: Meta | null }) {
  const isSupervisor = user.role === 'supervisor';
  // A supervisor WITH a brand is locked to it; one without (the default since 013) is not.
  const lockedBrand = isSupervisor ? (user.brandKey ?? null) : null;
  // A supervisor may mint only agent/supervisor accounts (and, when brand-locked, only in
  // their own brand).
  const roleOptions = isSupervisor
    ? ROLE_OPTIONS.filter((r) => r.value === 'agent' || r.value === 'supervisor')
    : ROLE_OPTIONS;

  const state = useAsync(() => api.listUsers(), []);
  const [form, setForm] = useState({
    username: '', displayName: '', role: 'agent' as Role,
    // Pre-fill a brand-locked supervisor's brand so their single-option brand select is
    // satisfied; a cross-brand supervisor (no brand) picks like an admin.
    brandKey: lockedBrand ?? '', password: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [edit, setEdit] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editOk, setEditOk] = useState('');

  const brandMode: BrandMode = roleOptions.find((r) => r.value === form.role)?.brand ?? 'none';
  const needsBrand = brandMode === 'required';
  const mayHaveBrand = brandMode !== 'none';
  // A brand-locked supervisor's brand is fixed to their own — the select shows it but
  // cannot be changed.
  const brandLocked = lockedBrand !== null;
  const brands = meta?.brands ?? [];

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(''); setOk('');
    try {
      await api.createUser({
        username: form.username.trim().toLowerCase(),
        displayName: form.displayName.trim(),
        role: form.role,
        // A brand-locked supervisor's target is always their own brand; otherwise honour the
        // picker (null for admin / cross-brand qms or supervisor). The server re-validates the
        // pair and the actor's reach.
        brandKey: brandLocked ? lockedBrand : (mayHaveBrand ? (form.brandKey || null) : null),
        password: form.password,
      });
      setOk(`${form.displayName} eklendi.`);
      setForm({
        username: '', displayName: '', role: 'agent',
        brandKey: lockedBrand ?? '', password: '',
      });
      state.reload();
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : 'Kullanıcı eklenemedi.');
    } finally { setBusy(false); }
  }

  const brandModeOf = (role: Role): BrandMode => ROLE_OPTIONS.find((r) => r.value === role)?.brand ?? 'none';

  function startEdit(u: ManagedUser) {
    setEditError(''); setEditOk(''); setOk('');
    // An agent row still in lower case (before migration 024) opens showing what Save will store.
    setEdit({ id: u.id, displayName: nameAs(u.role, u.displayName), role: u.role, brandKey: u.brandKey ?? '' });
  }

  async function saveEdit(u: ManagedUser) {
    if (!edit || saving) return;
    const mode = brandModeOf(edit.role);
    const brandKey = brandLocked ? lockedBrand : (mode === 'none' ? null : (edit.brandKey || null));
    const displayName = nameAs(edit.role, edit.displayName.trim());
    // Only what changed: role and brand travel TOGETHER (the server validates the pair).
    const patch: Parameters<typeof api.updateUser>[1] = {};
    if (displayName !== u.displayName) patch.displayName = displayName;
    if (edit.role !== u.role || brandKey !== (u.brandKey ?? null)) { patch.role = edit.role; patch.brandKey = brandKey; }
    if (!Object.keys(patch).length) { setEdit(null); return; }
    setSaving(true); setEditError('');
    try {
      const saved = await api.updateUser(u.id, patch);
      setEditOk(`${saved.displayName} güncellendi.`);
      setEdit(null);
      state.reload();
    } catch (err) {
      setEditError(err instanceof api.ApiError ? err.message : 'Güncellenemedi.');
    } finally { setSaving(false); }
  }

  async function toggleActive(u: ManagedUser) {
    setError('');
    try {
      await api.updateUser(u.id, { active: !u.active });
      state.reload();
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : 'Güncellenemedi.');
    }
  }

  async function resetPassword(u: ManagedUser) {
    const next = window.prompt(`${u.displayName} için yeni şifre:`);
    if (!next) return;
    setError('');
    try {
      await api.updateUser(u.id, { password: next });
      setOk(`${u.displayName} şifresi güncellendi — açık oturumları kapatıldı.`);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : 'Şifre güncellenemedi.');
    }
  }

  return (
    <>
      <PageHead icon="groups" title="Kullanıcılar" subtitle="Panel hesapları, rolleri ve marka yetkileri." />

      <Panel icon="person_add" title="Yeni kullanıcı" hint="Rol ve marka birlikte atanır">
        <form onSubmit={create}>
          <div className="formgrid">
            <div className="field">
              <label htmlFor="nu">Kullanıcı adı *</label>
              <input id="nu" className="inp" value={form.username} placeholder="ad.soyad" autoComplete="off"
                onChange={(e) => setForm({ ...form, username: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="nd">Ad Soyad *</label>
              <input id="nd" className="inp" value={form.displayName} autoComplete="off"
                onChange={(e) => setForm({ ...form, displayName: nameAs(form.role, e.target.value) })} />
            </div>
            <div className="field">
              <label htmlFor="nr">Rol *</label>
              <select id="nr" className="inp" value={form.role}
                onChange={(e) => setForm({
                  ...form, role: e.target.value as Role,
                  displayName: nameAs(e.target.value as Role, form.displayName),
                  brandKey: lockedBrand ?? '',
                })}>
                {roleOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="nb">Marka {needsBrand ? '*' : ''}</label>
              <select id="nb" className="inp" value={form.brandKey} disabled={!mayHaveBrand || brandLocked}
                onChange={(e) => setForm({ ...form, brandKey: e.target.value })}>
                <option value="">{needsBrand ? 'Seçin…' : 'Tüm markalar'}</option>
                {brands.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
              </select>
            </div>
            <div className="field wide">
              <label htmlFor="np">Şifre *</label>
              <input id="np" className="inp" type="text" value={form.password} autoComplete="off"
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="en az 8 karakter" />
            </div>
          </div>
          <div className="panel-foot" style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn pri" type="submit"
              disabled={busy || !form.username || !form.displayName || !form.password || (needsBrand && !form.brandKey)}
            >
              <Icon name="person_add" /><span>{busy ? 'Ekleniyor…' : 'Kullanıcı ekle'}</span>
            </button>
            {error ? <Note error>{error}</Note> : null}
            {ok ? <div className="callout good"><Icon name="check_circle" /><span>{ok}</span></div> : null}
          </div>
        </form>
      </Panel>

      <Panel icon="groups" title={state.data ? `${state.data.length} hesap` : 'Hesaplar'} hint="Pasif hesap giriş yapamaz; açık oturumları kapatılır">
        {state.loading && !state.data ? <Loading /> : null}
        {editError ? <div className="panel-body tight"><Note error>{editError}</Note></div> : null}
        {editOk ? <div className="panel-body tight"><Callout tone="good">{editOk}</Callout></div> : null}
        {edit?.role === 'agent' ? (
          <div className="panel-body tight">
            <Callout tone="warn" icon="info">Agent adı call-agent-admin'deki adıyla eşleşir (büyük/küçük harf önemsiz). Harfini değiştirirseniz skor beslemesi ve çağrı ataması o temsilciyi bulamaz.</Callout>
          </div>
        ) : null}
        {state.data && state.data.length === 0 ? <Empty>Hesap yok.</Empty> : null}
        {state.data && state.data.length > 0 ? (
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr><th>Ad Soyad</th><th>Kullanıcı adı</th><th>Rol</th><th>Marka</th><th>Durum</th><th /></tr>
              </thead>
              <tbody>
                {state.data.map((u) => (edit && edit.id === u.id ? (
                  <tr key={u.id}>
                    <td>
                      <input className="inp" aria-label="Ad Soyad" value={edit.displayName} autoComplete="off" style={{ minWidth: 170 }}
                        onChange={(e) => setEdit({ ...edit, displayName: nameAs(edit.role, e.target.value) })} />
                    </td>
                    <td className="tnum">{u.username}</td>
                    <td>
                      <select className="inp" aria-label="Rol" value={edit.role} disabled={u.username === user.username}
                        title={u.username === user.username ? 'Kendi rolünüzü değiştiremezsiniz' : undefined}
                        onChange={(e) => {
                          const role = e.target.value as Role;
                          setEdit({
                            ...edit, role, displayName: nameAs(role, edit.displayName),
                            brandKey: brandLocked ? (lockedBrand ?? '') : (brandModeOf(role) === 'none' ? '' : edit.brandKey),
                          });
                        }}>
                        {/* A supervisor's picker has no qms/admin — and their list holds no such account. */}
                        {roleOptions.map((r) => <option key={r.value} value={r.value}>{ROLE_NAME[r.value]}</option>)}
                      </select>
                    </td>
                    <td>
                      <select className="inp" aria-label="Marka" value={edit.brandKey}
                        disabled={brandModeOf(edit.role) === 'none' || brandLocked}
                        onChange={(e) => setEdit({ ...edit, brandKey: e.target.value })}>
                        <option value="">{brandModeOf(edit.role) === 'required' ? 'Seçin…' : 'Tüm markalar'}</option>
                        {brands.map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
                      </select>
                    </td>
                    <td><Pill tone={u.active ? 'good' : 'neu'}>{u.active ? 'Aktif' : 'Pasif'}</Pill></td>
                    <td className="r">
                      <span className="row" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                        <button className="iconbtn sm" type="button" title="Kaydet" onClick={() => saveEdit(u)}
                          disabled={saving || !edit.displayName.trim() || (brandModeOf(edit.role) === 'required' && !edit.brandKey && !brandLocked)}>
                          <Icon name="check" />
                        </button>
                        <button className="iconbtn sm" type="button" title="Vazgeç" onClick={() => { setEdit(null); setEditError(''); }}>
                          <Icon name="close" />
                        </button>
                      </span>
                    </td>
                  </tr>
                ) : (
                  <tr key={u.id} style={u.active ? undefined : { opacity: 0.55 }}>
                    <td>
                      <div className="who">
                        <Avatar name={u.displayName} kind={u.active ? 'accent' : 'neutral'} />
                        <span className="nm">{u.displayName}</span>
                      </div>
                    </td>
                    <td className="tnum">{u.username}</td>
                    <td><Pill tone={ROLE_TONE[u.role]}>{u.roleLabel}</Pill></td>
                    <td>{u.brandKey ? <Pill tone="neu">{u.brandKey}</Pill> : '—'}</td>
                    <td><Pill tone={u.active ? 'good' : 'neu'}>{u.active ? 'Aktif' : 'Pasif'}</Pill></td>
                    <td className="r">
                      <span className="row" style={{ justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                        <button className="iconbtn sm" type="button" title="Düzenle" onClick={() => startEdit(u)}>
                          <Icon name="edit_note" />
                        </button>
                        <button className="iconbtn sm" type="button" title="Şifre sıfırla" onClick={() => resetPassword(u)}>
                          <Icon name="key" />
                        </button>
                        <button className="iconbtn sm" type="button" title={u.active ? 'Pasife al' : 'Aktifleştir'} onClick={() => toggleActive(u)}>
                          <Icon name={u.active ? 'block' : 'task_alt'} />
                        </button>
                      </span>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Panel>
    </>
  );
}
